import {createContext, useCallback, useContext, useEffect, useState} from 'react'

import {type FeedDescriptor} from '#/state/queries/post-feed'
import {useSession} from '#/state/session'
import {IS_WEB} from '#/env'
import {account} from '#/storage'

type StateContext = FeedDescriptor | null
type SetContext = (v: FeedDescriptor) => void
type SelectedFeedState = {
  accountDid?: string
  feed: FeedDescriptor | null
}

const SESSION_FEED_KEY_PREFIX = 'lastSelectedHomeFeed:'

function sessionFeedKey(did?: string) {
  return SESSION_FEED_KEY_PREFIX + (did ?? 'logged-out')
}

const stateContext = createContext<StateContext>(null)
stateContext.displayName = 'SelectedFeedStateContext'
const setContext = createContext<SetContext>((_: string) => {})
setContext.displayName = 'SelectedFeedSetContext'

function getInitialFeed(did?: string): FeedDescriptor | null {
  if (IS_WEB) {
    if (window.location.pathname === '/') {
      const params = new URLSearchParams(window.location.search)
      const feedFromUrl = params.get('feed')
      if (feedFromUrl) {
        // If explicitly booted from a link like /?feed=..., prefer that.
        return feedFromUrl as FeedDescriptor
      }
    }

    const feedFromSession = sessionStorage.getItem(sessionFeedKey(did))
    if (feedFromSession) {
      // Fall back to a previously chosen feed for this browser tab.
      return feedFromSession as FeedDescriptor
    }
  }

  if (did) {
    const feedFromStorage = account.get([did, 'lastSelectedHomeFeed'])
    if (feedFromStorage) {
      // Fall back to the last chosen one across all tabs.
      return feedFromStorage as FeedDescriptor
    }
  }

  return null
}

export function Provider({children}: React.PropsWithChildren<{}>) {
  const {currentAccount} = useSession()
  const accountDid = currentAccount?.did
  const [state, setState] = useState<SelectedFeedState>(() => ({
    accountDid,
    feed: getInitialFeed(accountDid),
  }))

  useEffect(() => {
    // Feed selection is account-local. Reset immediately so a sign-in or
    // account switch cannot display the previous account's selected feed.
    setState({accountDid, feed: getInitialFeed(accountDid)})
  }, [accountDid])

  // Effects run after paint. Guard the rendered value as well so a previous
  // account's selected feed cannot be used for even an intermediate render.
  const selectedFeed =
    state.accountDid === accountDid ? state.feed : getInitialFeed(accountDid)

  const saveState = useCallback(
    (feed: FeedDescriptor) => {
      setState({accountDid, feed})
      if (IS_WEB) {
        try {
          sessionStorage.setItem(sessionFeedKey(accountDid), feed)
        } catch {}
      }
      if (accountDid) {
        account.set([accountDid, 'lastSelectedHomeFeed'], feed)
      }
    },
    [accountDid],
  )

  return (
    <stateContext.Provider value={selectedFeed}>
      <setContext.Provider value={saveState}>{children}</setContext.Provider>
    </stateContext.Provider>
  )
}

export function useSelectedFeed() {
  return useContext(stateContext)
}

export function useSetSelectedFeed() {
  return useContext(setContext)
}
